import { Box, useTheme, alpha } from "@mui/material";
import GaugeDial from "./GaugeDial";

const GREEN = "#2e9d5a";
const AMBER = "#e0a11b";
const RED = "#e5484d";

// Semantic color for a value against good/warn thresholds (null → neutral).
const toneColor = (v, good, warn, higherWorse = false) => {
  if (v === null || v === undefined) return undefined;
  if (higherWorse) return v <= good ? GREEN : v <= warn ? AMBER : RED;
  return v >= good ? GREEN : v >= warn ? AMBER : RED;
};

/** eCalc-style dial row for a /simulate result's headline metrics. */
const GaugeRow = ({ result }) => {
  const theme = useTheme();
  const sys = result?.system;
  if (!sys) return null;

  const hoverPowerW = result.hover ? Math.round((result.hover.electric_power_w || 0) * sys.num_motors) : null;
  const maxPowerW = result.max ? (result.max.electric_power_w || 0) * sys.num_motors : null;

  const gauges = [
    {
      label: "Thrust : Weight",
      value: sys.thrust_to_weight,
      unit: ": 1",
      fraction: (sys.thrust_to_weight || 0) / 4,
      color: toneColor(sys.thrust_to_weight, 2, 1.3),
    },
    {
      label: "Hover throttle",
      value: sys.hover_throttle_pct,
      unit: "%",
      fraction: (sys.hover_throttle_pct || 0) / 100,
      color: toneColor(sys.hover_throttle_pct, 60, 80, true),
    },
    {
      label: "Hover flight time",
      value: sys.hover_flight_time_min,
      unit: "min",
      fraction: (sys.hover_flight_time_min || 0) / 25,
      color: toneColor(sys.hover_flight_time_min, 12, 6),
    },
    {
      label: "Efficiency",
      value: sys.specific_thrust_hover_g_per_w,
      unit: "g/W",
      fraction: (sys.specific_thrust_hover_g_per_w || 0) / 12,
      color: toneColor(sys.specific_thrust_hover_g_per_w, 6, 3),
    },
    {
      label: "Hover power",
      value: hoverPowerW,
      unit: "W",
      fraction: maxPowerW ? (hoverPowerW || 0) / maxPowerW : 0,
      color: undefined,
    },
    {
      label: "Max thrust",
      value: sys.max_total_thrust_g,
      unit: "g",
      fraction: sys.all_up_weight_g ? Math.min((sys.max_total_thrust_g || 0) / (sys.all_up_weight_g * 4), 1) : 0,
      color: undefined,
    },
    {
      label: "Pitch speed",
      value: sys.pitch_speed_kmh,
      unit: "km/h",
      fraction: Math.min((sys.pitch_speed_kmh || 0) / 200, 1),
      color: undefined,
    },
    ...(sys.est_temp_max_c != null
      ? [
          {
            label: "est. Temperature",
            value: sys.est_temp_max_c,
            unit: "°C @ max",
            fraction: Math.min((sys.est_temp_max_c || 0) / 120, 1),
            color: toneColor(sys.est_temp_max_c, 60, 90, true),
          },
        ]
      : []),
  ];

  return (
    <Box
      sx={{
        display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "space-around",
        p: 2, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.1 : 0.6),
      }}
    >
      {gauges.map((g) => (
        <GaugeDial key={g.label} {...g} />
      ))}
    </Box>
  );
};

export default GaugeRow;
